import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  Drawer,
  Fab,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import PageContainer from '../../../components/PageContainer';
import AppDataTable from '../../../components/AppDataTable';
import {
  getClientes,
  insertarCliente,
  actualizarCliente,
  eliminarCliente,
  lookupNitTekra,
} from '../../../services/modules/Clientes';

const Clientes = () => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const autoFilledRef = useRef({ nombre: false, direccion: false, dpi: false });
  const [clientes, setClientes] = useState([]);
  const [filteredClientes, setFilteredClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [search, setSearch] = useState('');
  const [nitLookupState, setNitLookupState] = useState({
    loading: false,
    message: '',
    notFound: false,
    lastNit: '',
  });

  const [formData, setFormData] = useState({
    id_cliente: null,
    nombre: '',
    dpi: '',
    nit: '',
    direccion: '',
    telefono: '',
    correo: '',
    codigo_postal: '',
    municipio: '',
    departamento: '',
    pais: 'GT',
    tiene_credito: 0,
  });

  useEffect(() => {
    const fetchClientes = async () => {
      setLoading(true);
      try {
        const data = await getClientes();
        setClientes(data || []);
        setFilteredClientes(data || []);
      } catch (err) {
        setError('No pudimos cargar los clientes.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchClientes();
  }, []);

  useEffect(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      setFilteredClientes(clientes);
      return;
    }
    setFilteredClientes(
      (clientes || []).filter((cliente) =>
        [cliente.nombre, cliente.dpi, cliente.nit, cliente.telefono]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query))
      )
    );
  }, [search, clientes]);

  const handleOpenPanel = (cliente = null) => {
    setNitLookupState({ loading: false, message: '', notFound: false, lastNit: '' });
    autoFilledRef.current = { nombre: false, direccion: false, dpi: false };
    if (cliente) {
      setFormData({
        ...cliente,
        tiene_credito: cliente.tiene_credito ? 1 : 0,
      });
    } else {
      setFormData({
        id_cliente: null,
        nombre: '',
        dpi: '',
        nit: '',
        direccion: '',
        telefono: '',
        correo: '',
        codigo_postal: '',
        municipio: '',
        departamento: '',
        pais: 'GT',
        tiene_credito: 0,
      });
    }
    setDrawerOpen(true);
  };

  const handleClosePanel = () => setDrawerOpen(false);

  const performNitLookup = async ({ nit, showAlertOnNotFound = false } = {}) => {
    const normalizedNit = String(nit ?? formData.nit ?? '').trim();
    if (!normalizedNit) return { skipped: true };
    if (normalizedNit === nitLookupState.lastNit && !nitLookupState.loading) {
      return { cached: true, found: !nitLookupState.notFound };
    }

    setNitLookupState((prev) => ({
      ...prev,
      loading: true,
      message: '',
      notFound: false,
      lastNit: normalizedNit,
    }));

    try {
      const res = await lookupNitTekra(normalizedNit);
      if (!res?.found) {
        const message = 'Error: el NIT no existe';
        setNitLookupState({ loading: false, message, notFound: true, lastNit: normalizedNit });
        if (showAlertOnNotFound) alert(message);
        return { found: false };
      }

      setFormData((prev) => ({
        ...prev,
        nombre: (!prev.nombre || autoFilledRef.current.nombre) ? (res.nombre || prev.nombre) : prev.nombre,
        direccion: (!prev.direccion || autoFilledRef.current.direccion) ? (res.direccion || prev.direccion) : prev.direccion,
        dpi: (!prev.dpi || autoFilledRef.current.dpi) ? (res.cui || prev.dpi) : prev.dpi,
      }));

      autoFilledRef.current = {
        nombre: Boolean(res.nombre),
        direccion: Boolean(res.direccion),
        dpi: Boolean(res.cui),
      };

      const estado = String(res.estado || '').trim().toUpperCase();
      if (estado && estado !== 'ACTIVO') {
        setNitLookupState({ loading: false, message: `NIT ${estado}`, notFound: false, lastNit: normalizedNit });
      } else {
        setNitLookupState({ loading: false, message: '', notFound: false, lastNit: normalizedNit });
      }

      return { found: true, estado };
    } catch (err) {
      const message = err?.message || 'No se pudo consultar el NIT';
      setNitLookupState({ loading: false, message, notFound: false, lastNit: normalizedNit });
      return { found: null, error: true };
    }
  };

  const handleNitBlur = async () => performNitLookup({ showAlertOnNotFound: true });

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    if (name === 'nit') {
      // Al cambiar NIT: iniciar "nueva búsqueda" (limpia datos consultados previos).
      setFormData((prev) => ({
        ...prev,
        nit: value,
        nombre: '',
        direccion: '',
        dpi: '',
      }));
      autoFilledRef.current = { nombre: false, direccion: false, dpi: false };
      setNitLookupState({ loading: false, message: '', notFound: false, lastNit: '' });
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value,
    }));

    if (name === 'nombre' || name === 'direccion' || name === 'dpi') {
      autoFilledRef.current = { ...autoFilledRef.current, [name]: false };
    }
  };

  const refreshClientes = async () => {
    try {
      const data = await getClientes();
      setClientes(data || []);
      setFilteredClientes(data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmit = async () => {
    try {
      setProcessing(true);

      const nit = String(formData.nit || '').trim();
      if (nit) {
        const lookup = await performNitLookup({ nit, showAlertOnNotFound: true });
        if (lookup?.found === false) return;
      }

      if (formData.id_cliente) {
        await actualizarCliente(formData);
      } else {
        await insertarCliente(formData);
      }
      await refreshClientes();
      setDrawerOpen(false);
    } catch (err) {
      console.error(err);
      alert(err?.message || 'Hubo un error al guardar la informacion.');
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm('Deseas eliminar este cliente?');
    if (!confirmed) return;
    try {
      setProcessing(true);
      await eliminarCliente(id);
      await refreshClientes();
    } catch (err) {
      console.error(err);
      alert('No pudimos eliminar el registro.');
    } finally {
      setProcessing(false);
    }
  };

  const metrics = useMemo(() => {
    const total = clientes.length;
    const credit = clientes.filter((c) => Number(c.tiene_credito) === 1).length;
    const totalSaldo = clientes.reduce((acc, c) => acc + Number(c.Saldo || 0), 0);
    return [
      {
        label: 'Clientes activos',
        value: total,
        helper: `${credit} con credito`,
      },
      {
        label: 'Saldo por cobrar',
        value: `Q ${totalSaldo.toLocaleString('es-GT', { minimumFractionDigits: 2 })}`,
        helper: 'Incluye ventas al credito registradas',
      },
      {
        label: 'Tasa de credito',
        value: total ? `${Math.round((credit / total) * 100)}%` : '0%',
        helper: 'Clientes con linea aprobada',
      },
    ];
  }, [clientes]);

  const columns = useMemo(
    () => [
      {
        field: 'cliente',
        headerName: 'Cliente',
        flex: 1.6,
        minWidth: 240,
        sortable: true,
        valueGetter: (_value, row) => row?.nombre || '',
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ width: 36, height: 36 }}>
              {row.nombre?.[0]?.toUpperCase() || '?'}
            </Avatar>
            <Box>
              <Typography variant="body1" fontWeight={600}>
                {row.nombre}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                NIT {row.nit || 'N/D'}
              </Typography>
            </Box>
          </Stack>
        ),
      },
      {
        field: 'contacto',
        headerName: 'Contacto',
        flex: 1.2,
        minWidth: 200,
        sortable: false,
        valueGetter: (_value, row) => row?.telefono || '',
        renderCell: ({ row }) => (
          <Stack spacing={0.5}>
            <Typography variant="body2">{row.telefono || 'Sin telefono'}</Typography>
            <Typography variant="caption" color="text.secondary">
              {row.correo || 'Sin correo'}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'saldo',
        headerName: 'Saldo',
        flex: 0.9,
        minWidth: 170,
        sortable: true,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_value, row) => Number(row?.Saldo || 0),
        renderCell: ({ row }) => (
          <Stack spacing={0.5} alignItems="flex-end">
            <Typography variant="body1" fontWeight={600}>
              Q {Number(row.Saldo || 0).toLocaleString('es-GT', { minimumFractionDigits: 2 })}
            </Typography>
            <Chip
              size="small"
              label={row.tiene_credito ? 'Credito activo' : 'Contado'}
              color={row.tiene_credito ? 'success' : 'default'}
            />
          </Stack>
        ),
      },
      {
        field: 'acciones',
        headerName: 'Acciones',
        minWidth: 160,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={1}>
            <Tooltip title="Editar">
              <IconButton size="small" onClick={() => handleOpenPanel(row)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Eliminar">
              <IconButton size="small" color="error" onClick={() => handleDelete(row.id_cliente)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [handleDelete]
  );

  return (
    <PageContainer>
      <Stack spacing={3}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          {metrics.map((metric) => (
            <Card key={metric.label} sx={{ flex: 1 }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  {metric.label}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
                  {metric.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {metric.helper}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Stack>

        {error && <Alert severity="error">{error}</Alert>}

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <TextField
            placeholder="Buscar por nombre, DPI, NIT o telefono"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => setSearch('')}>
              Limpiar
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenPanel()}>
              Nuevo cliente
            </Button>
          </Stack>
        </Stack>

        {isXs ? (
          <Grid container spacing={2}>
            {(loading ? Array.from({ length: 3 }) : filteredClientes).map((row, index) =>
              loading ? (
                <Grid item xs={12} key={index}>
                  <Skeleton variant="rounded" height={140} />
                </Grid>
              ) : (
                <Grid item xs={12} key={row.id_cliente}>
                  <Card>
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {row.nombre}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            DPI {row.dpi || 'N/D'} · NIT {row.nit || 'N/D'}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                          <IconButton size="small" onClick={() => handleOpenPanel(row)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(row.id_cliente)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Stack>
                      <Divider sx={{ my: 1.5 }} />
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Chip
                          size="small"
                          color={row.tiene_credito ? 'success' : 'default'}
                          label={row.tiene_credito ? 'Credito activo' : 'Contado'}
                        />
                        <Typography variant="subtitle2" fontWeight={600}>
                          Q {Number(row.Saldo || 0).toFixed(2)}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              )
            )}
          </Grid>
        ) : (
          <AppDataTable
            rows={filteredClientes}
            columns={columns}
            loading={loading}
          />
        )}
      </Stack>

      {!isXs && (
        <Fab
          color="primary"
          onClick={() => handleOpenPanel()}
          sx={{
            position: 'fixed',
            bottom: 32,
            right: 32,
            boxShadow: 6,
          }}
        >
          <AddIcon />
        </Fab>
      )}

      <Drawer
        anchor={isXs ? 'bottom' : 'right'}
        open={drawerOpen}
        onClose={handleClosePanel}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 420 },
            borderRadius: isXs ? '24px 24px 0 0' : '24px 0 0 24px',
          },
        }}
      >
        <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6">
                {formData.id_cliente ? 'Editar cliente' : 'Nuevo cliente'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Completa la informacion para continuar.
              </Typography>
            </Box>
            <IconButton onClick={handleClosePanel}>
              <CloseIcon />
            </IconButton>
          </Stack>

          <Stack spacing={2} sx={{ flex: 1, overflowY: 'auto', pr: 1 }}>
            <TextField
              label="NIT"
              name="nit"
              value={formData.nit}
              onChange={handleChange}
              onBlur={handleNitBlur}
              autoFocus={!formData.id_cliente}
              helperText={nitLookupState.loading ? 'Consultando NIT...' : nitLookupState.message}
              error={Boolean(nitLookupState.message)}
              fullWidth
              size="small"
            />
            <TextField label="Nombre" name="nombre" value={formData.nombre} onChange={handleChange} fullWidth size="small" />
            <TextField label="Direccion" name="direccion" value={formData.direccion} onChange={handleChange} fullWidth size="small" />
            <TextField label="DPI" name="dpi" value={formData.dpi} onChange={handleChange} fullWidth size="small" />
            <TextField label="Telefono" name="telefono" value={formData.telefono} onChange={handleChange} fullWidth size="small" />
            <TextField label="Correo" name="correo" value={formData.correo} onChange={handleChange} fullWidth size="small" />
            <Stack direction="row" spacing={1}>
              <TextField label="Codigo postal" name="codigo_postal" value={formData.codigo_postal} onChange={handleChange} fullWidth size="small" />
              <TextField label="Pais" name="pais" value={formData.pais} onChange={handleChange} fullWidth size="small" />
            </Stack>
            <TextField label="Municipio" name="municipio" value={formData.municipio} onChange={handleChange} fullWidth size="small" />
            <TextField label="Departamento" name="departamento" value={formData.departamento} onChange={handleChange} fullWidth size="small" />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.tiene_credito === 1}
                  onChange={handleChange}
                  name="tiene_credito"
                />
              }
              label="Cliente con credito"
            />
          </Stack>

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={handleClosePanel} color="inherit">
              Cancelar
            </Button>
            <Button variant="contained" onClick={handleSubmit} disabled={processing}>
              {processing ? 'Guardando...' : 'Guardar'}
            </Button>
          </Stack>
        </Box>
      </Drawer>
    </PageContainer>
  );
};

export default Clientes;
